// ═══════════════════════════════════════════════════════════════════════════════
// MULTI-CHANNEL CONTACT ENGINE v1.0
// EnergiPro Solutions — WhatsApp + Phone + Calendar Integration
// ═══════════════════════════════════════════════════════════════════════════════
// Channels: WhatsApp (primary), Direct Call, Calendar Booking
// Priority Logic: High → All | Medium → WA + Calendar | Low → WA only
// Tracking: All interactions logged to RFQ activity
// ═══════════════════════════════════════════════════════════════════════════════

import { PriorityLevel } from './rfqScoringEngine';

// ─── CONFIGURATION ───────────────────────────────────────────────────────────

export const CONTACT_CONFIG = {
  whatsapp: {
    number: '966552693829',
    baseUrl: 'https://wa.me/966552693829',
  },
  phone: {
    number: '+966133308579',
    display: '+966 13 330 8579',
    telLink: 'tel:+966133308579',
  },
  calendar: {
    // Calendly or Google Calendar link
    baseUrl: 'https://calendly.com/energiprogroup-sales/new-meeting',
    label: 'Schedule Technical Call',
  },
};

// ─── TYPES ───────────────────────────────────────────────────────────────────

export type ContactChannel = 'whatsapp' | 'phone' | 'calendar';

export interface ContactInteraction {
  id: string;
  rfqId: string;
  channel: ContactChannel;
  timestamp: number;
  metadata?: Record<string, string>;
}

export interface ChannelVisibility {
  whatsapp: boolean;
  phone: boolean;
  calendar: boolean;
}

export interface ContactLink {
  channel: ContactChannel;
  url: string;
  label: string;
  icon: string;
  description: string;
  priority: 'primary' | 'secondary';
}

// ─── PRIORITY-BASED CHANNEL VISIBILITY ───────────────────────────────────────

export function getChannelVisibility(priority: PriorityLevel): ChannelVisibility {
  switch (priority) {
    case 'high':
      return { whatsapp: true, phone: true, calendar: true };
    case 'medium':
      return { whatsapp: true, phone: false, calendar: true };
    case 'low':
      return { whatsapp: true, phone: false, calendar: false };
    default:
      return { whatsapp: true, phone: false, calendar: false };
  }
}

// ─── LINK GENERATORS ─────────────────────────────────────────────────────────

export function generateWhatsAppLink(rfqId?: string, serviceName?: string): string {
  let message = 'Hello EnergiPro,';
  if (rfqId) {
    message += ` regarding RFQ ${rfqId}`;
  }
  if (serviceName) {
    message += ` — ${serviceName}`;
  }
  if (!rfqId && !serviceName) {
    message += ' I would like to discuss a technical requirement.';
  }
  return `${CONTACT_CONFIG.whatsapp.baseUrl}?text=${encodeURIComponent(message)}`;
}

export function generatePhoneLink(): string {
  return CONTACT_CONFIG.phone.telLink;
}

export function generateCalendarLink(rfqId?: string, serviceName?: string): string {
  let url = CONTACT_CONFIG.calendar.baseUrl;
  const params: string[] = [];
  if (rfqId) {
    params.push(`name=RFQ ${rfqId}`);
  }
  if (serviceName) {
    params.push(`a1=${encodeURIComponent(serviceName)}`);
  }
  if (params.length > 0) {
    url += `?${params.join('&')}`;
  }
  return url;
}

// ─── CONTACT LINKS BUILDER ──────────────────────────────────────────────────

export function buildContactLinks(
  priority: PriorityLevel,
  rfqId?: string,
  serviceName?: string
): ContactLink[] {
  const visibility = getChannelVisibility(priority);
  const links: ContactLink[] = [];

  if (visibility.whatsapp) {
    links.push({
      channel: 'whatsapp',
      url: generateWhatsAppLink(rfqId, serviceName),
      label: 'WhatsApp',
      icon: 'whatsapp',
      description: 'Message us directly on WhatsApp',
      priority: 'primary',
    });
  }

  if (visibility.phone) {
    links.push({
      channel: 'phone',
      url: generatePhoneLink(),
      label: 'Call Now',
      icon: 'phone',
      description: `Direct: ${CONTACT_CONFIG.phone.display}`,
      priority: 'secondary',
    });
  }

  if (visibility.calendar) {
    links.push({
      channel: 'calendar',
      url: generateCalendarLink(rfqId, serviceName),
      label: 'Schedule Technical Call',
      icon: 'calendar',
      description: 'Book a call with our engineering team',
      priority: 'secondary',
    });
  }

  return links;
}

// ─── ACTIVITY TRACKING ───────────────────────────────────────────────────────

const STORAGE_KEY = 'energipro_contact_interactions';

export function trackContactInteraction(
  rfqId: string,
  channel: ContactChannel,
  metadata?: Record<string, string>
): ContactInteraction {
  const interaction: ContactInteraction = {
    id: `ci-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    rfqId,
    channel,
    timestamp: Date.now(),
    metadata,
  };

  // Store in localStorage for persistence
  const existing = getContactInteractions();
  existing.push(interaction);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
  } catch {
    // Silent fail if storage full
  }

  return interaction;
}

export function getContactInteractions(rfqId?: string): ContactInteraction[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return [];
    const all: ContactInteraction[] = JSON.parse(data);
    if (rfqId) return all.filter(i => i.rfqId === rfqId);
    return all;
  } catch {
    return [];
  }
}

export function getInteractionStats(rfqId?: string): Record<ContactChannel, number> {
  const interactions = getContactInteractions(rfqId);
  return {
    whatsapp: interactions.filter(i => i.channel === 'whatsapp').length,
    phone: interactions.filter(i => i.channel === 'phone').length,
    calendar: interactions.filter(i => i.channel === 'calendar').length,
  };
}

// ─── EMAIL CONTENT BLOCKS ────────────────────────────────────────────────────

export function generateEmailContactBlock(
  priority: PriorityLevel,
  rfqId: string,
  serviceName?: string
): string {
  const visibility = getChannelVisibility(priority);
  const waLink = generateWhatsAppLink(rfqId, serviceName);
  const calLink = generateCalendarLink(rfqId, serviceName);

  let block = `\n━━━ DIRECT COMMUNICATION CHANNELS ━━━\n`;
  block += `\n`;

  if (visibility.whatsapp) {
    block += `📱 WhatsApp (Instant): ${waLink}\n`;
    block += `   Message our team directly for immediate response.\n\n`;
  }

  if (visibility.phone) {
    block += `📞 Direct Call: ${CONTACT_CONFIG.phone.display}\n`;
    block += `   Priority line — available for high-priority technical discussions.\n\n`;
  }

  if (visibility.calendar) {
    block += `📅 Schedule Technical Call: ${calLink}\n`;
    block += `   Book a dedicated call with our engineering team.\n\n`;
  }

  block += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;

  return block;
}

// ─── PROPOSAL CONTACT SECTION ────────────────────────────────────────────────

export function generateProposalContactSection(rfqId: string, serviceName?: string): string {
  const waLink = generateWhatsAppLink(rfqId, serviceName);
  const calLink = generateCalendarLink(rfqId, serviceName);

  return `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
CONTACT & NEXT STEPS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

To proceed with this proposal or discuss technical requirements:

📱 WhatsApp (Fastest Response):
   ${waLink}

📞 Direct Line:
   ${CONTACT_CONFIG.phone.display}

📅 Schedule Engineering Discussion:
   ${calLink}

📧 Email:
   rfq@energiprogroup.com
   Reference: ${rfqId}

🌐 Client Portal:
   Track status at portal.energiprogroup.com

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`;
}

// ─── WIDGET POSITION HELPERS ─────────────────────────────────────────────────

export type WidgetPosition = 'bottom-right' | 'bottom-left';

export function getWidgetPosition(): WidgetPosition {
  return 'bottom-right';
}
